/**
 * TunnelStatusCard
 *
 * Single tunnel card: status dot, bandwidth, BGP, packet loss.
 */

import { Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import type { TunnelInfo } from '../../types/security.types';

const STATUS_CONFIG = {
  up: { color: 'bg-ems-green', label: 'Connected', icon: Wifi, border: 'border-ems-green/20' },
  degraded: { color: 'bg-ems-amber', label: 'Degraded', icon: AlertTriangle, border: 'border-ems-amber/20' },
  down: { color: 'bg-ems-red', label: 'Disconnected', icon: WifiOff, border: 'border-ems-red/20' },
  unknown: { color: 'bg-ems-text-muted', label: 'Unknown', icon: WifiOff, border: 'border-ems-border' },
};

function formatBandwidth(bps: number): string {
  if (bps === 0) return '0 bps';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(1)} Kbps`;
  return `${bps.toFixed(0)} bps`;
}

interface TunnelStatusCardProps {
  tunnel: TunnelInfo;
  onClick?: (siteId: string) => void;
}

export function TunnelStatusCard({ tunnel, onClick }: TunnelStatusCardProps) {
  const statusConfig = STATUS_CONFIG[tunnel.tunnel_status] || STATUS_CONFIG.unknown;
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`bg-ems-bg-card rounded-2xl border ${statusConfig.border} p-4 cursor-pointer hover:bg-ems-bg-card-hover transition-colors`}
      onClick={() => onClick?.(tunnel.site_id)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${statusConfig.color}`} />
          <h3 className="text-sm font-semibold text-ems-text-primary truncate">{tunnel.site_name}</h3>
        </div>
        <StatusIcon className={`w-4 h-4 ${
          tunnel.tunnel_status === 'up' ? 'text-ems-green' :
          tunnel.tunnel_status === 'degraded' ? 'text-ems-amber' :
          'text-ems-red'
        }`} />
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-ems-text-muted">Status</span>
          <span className="font-medium text-ems-text-primary">{statusConfig.label}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ems-text-muted">BGP</span>
          <span className={`font-medium ${
            tunnel.bgp_status === 'established' ? 'text-ems-green' : 'text-ems-red'
          }`}>
            {tunnel.bgp_status === 'established' ? 'Established' : 'Down'}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ems-text-muted">Bandwidth</span>
          <span className="font-medium text-ems-text-primary">{formatBandwidth(tunnel.bandwidth_bps)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ems-text-muted">Packet Loss</span>
          <span className={`font-medium ${
            tunnel.packet_loss_pct > 1 ? 'text-ems-red' :
            tunnel.packet_loss_pct > 0 ? 'text-ems-amber' :
            'text-ems-green'
          }`}>
            {tunnel.packet_loss_pct.toFixed(2)}%
          </span>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-ems-border">
        <span className="text-[10px] text-ems-text-muted font-mono">{tunnel.site_code}</span>
      </div>
    </div>
  );
}
